import type { AxiosInstance } from "axios";
import * as Sentry from "@sentry/nextjs";
import { activeSessionTelemetryScope } from "@/components/codesmith/sessionTelemetryScope";

/**
 * Reports only while a session page is open:
 *
 *     connected → connecting   drop,      state=connecting
 *     connecting → connected   reconnect, gap_ms=<down time>
 */
const METRICS_URL = "/frontend/metrics";
const CONNECTED = "connected";
/** A flapping socket on one page load stops reporting past this. */
const MAX_REPORTS_PER_PAGE = 40;

let api: AxiosInstance | null = null;
let droppedAt: number | null = null;
let dropReportedFor: string | null = null;
let reports = 0;

export function installWsConnectionTelemetry(nextApi: AxiosInstance): void {
  api = nextApi;
}

export function observeWsStateChange(previous: string, current: string): void {
  const now = Date.now();
  if (previous === CONNECTED && current !== CONNECTED) {
    droppedAt = now;
    dropReportedFor = report("drop", current, null);
    return;
  }
  if (current === CONNECTED && droppedAt !== null) {
    const gapMs = Math.max(0, Math.round(now - droppedAt));
    const reconnectedOnDropSession =
      dropReportedFor !== null &&
      activeSessionTelemetryScope()?.sessionId === dropReportedFor;
    droppedAt = null;
    dropReportedFor = null;
    if (reconnectedOnDropSession) report("reconnect", current, gapMs);
  }
}

function report(
  transition: "drop" | "reconnect",
  state: string,
  gapMs: number | null,
): string | null {
  const scope = activeSessionTelemetryScope();
  if (!scope || reports >= MAX_REPORTS_PER_PAGE) return null;
  reports += 1;

  Sentry.addBreadcrumb({
    category: "ws",
    level: transition === "drop" ? "warning" : "info",
    message: `Reverb ${transition}`,
    data: { state, gap_ms: gapMs, session_id: scope.sessionId },
  });

  void api
    ?.post(METRICS_URL, {
      codesmith_event: "ws_connection",
      codesmith_session_id: scope.sessionId,
      codesmith_ws_transition: transition,
      codesmith_ws_state: state,
      ...(gapMs === null ? {} : { codesmith_ws_gap_ms: gapMs }),
    })
    .catch(() => {});
  return scope.sessionId;
}
